import { useEffect } from "react";
import {
  Button,
  Flex,
  Input,
  Modal,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Form, { OnValidSubmitHandler } from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import InlineFieldsWrapper from "components/Shared/Forms/InlineFieldsWrapper";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import NumberInput from "components/Shared/Forms/NumberInput";
import SwitchInput from "components/Shared/Forms/SwitchInput";
import { ProductLineInput } from "__generated__/gql/graphql";
import type { Writeable } from "utils/typescriptUtilis";
import { formatInteger, makeFormatCurrency } from "utils/formatting";
import * as productLineSchemaFields from "utils/schemas/productLine";
import NamingOptions from "components/Products/NamingOptions";
import ManufacturingFacilitySelect from "components/Shared/Select/ManufacturingFacilitySelect";
import ProductCategoriesSelect from "./ProductCategoriesSelect";
import CalculatedField from "./CalculatedField";
import ManageManufacturingFacilitiesModalContent from "./ManageManufacturingFacilitiesModal";

export const FORM_ID = "product-line-form";

export const productLineFields = [
  "name",
  "shortName",
  "nameSeparator",
  "isInternalSkuIdAppended",
  "wholesaleUnitPrice",
  "manufacturingFacilityId",
  "shipsFromZip",
  "innerCaseQuantity",
  "wholesaleCaseQuantity",
] as const;

type ProductLinePrimitiveInputs = Required<
  Pick<Writeable<ProductLineInput>, (typeof productLineFields)[number]>
>;

export type ProductLineInputs = ProductLinePrimitiveInputs & {
  productCategoryIds?: string[];
};

const productLineSchema: yup.ObjectSchema<ProductLineInputs> = yup
  .object()
  .shape({
    name: productLineSchemaFields.name,
    shortName: productLineSchemaFields.shortName.defined(),
    nameSeparator: productLineSchemaFields.nameSeparator.defined(),
    isInternalSkuIdAppended:
      productLineSchemaFields.isInternalSkuIdAppended.defined(),
    productCategoryIds: productLineSchemaFields.productCategoryIds,
    manufacturingFacilityId: productLineSchemaFields.manufacturingFacilityId,
    shipsFromZip: productLineSchemaFields.shipsFromZip,
    wholesaleUnitPrice: productLineSchemaFields.wholesaleUnitPrice,
    innerCaseQuantity: productLineSchemaFields.innerCaseQuantity,
    wholesaleCaseQuantity: productLineSchemaFields.wholesaleCaseQuantity,
  })
  .required();

const { FormField, ControlledFormField } =
  createFormFieldComponents<ProductLineInputs>({
    schema: productLineSchema,
  });

export const processApolloError =
  makePostgraphileErrorProcessor<ProductLinePrimitiveInputs>(
    getPostgraphileErrorMatchersForFields(productLineFields, {
      type: "ProductLine",
    }),
  );

type Props = {
  defaultValues?: ProductLineInputs;
  isCreateNew?: boolean;
  isLoading?: boolean;
  onValidSubmit: OnValidSubmitHandler<ProductLineInputs>;
};

export default function ProductLinePanelForm({
  defaultValues,
  isCreateNew = false,
  isLoading = false,
  onValidSubmit,
}: Props) {
  const {
    isOpen: isManageFacilitiesOpen,
    onOpen: onOpenManageFacilities,
    onClose: onCloseManageFacilities,
  } = useDisclosure();

  const formContext = useForm<ProductLineInputs>({
    resolver: yupResolver(productLineSchema),
    defaultValues: {
      nameSeparator: productLineSchemaFields.DEFAULT_NAME_SEPARATOR,
      isInternalSkuIdAppended: false,
    },
  });
  const { reset } = formContext;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isCreateNew) formContext.setFocus("name");
  }, [formContext, isCreateNew]);

  const [
    name,
    shortName,
    nameSeparator,
    isInternalSkuIdAppended,
    innerCaseQuantity,
    wholesaleCaseQuantity,
    wholesaleUnitPrice,
  ] = formContext.watch([
    "name",
    "shortName",
    "nameSeparator",
    "isInternalSkuIdAppended",
    "innerCaseQuantity",
    "wholesaleCaseQuantity",
    "wholesaleUnitPrice",
  ]);

  const wholesaleCaseQuantityUnits = wholesaleCaseQuantity
    ? (innerCaseQuantity ?? 1) * wholesaleCaseQuantity
    : null;

  return (
    <Form
      id={FORM_ID}
      {...{ formContext, onValidSubmit }}
      isLoaded={!isLoading || isCreateNew}
    >
      <FormField name="name" id="ProductLinePanel-product-line-name" />

      <NamingOptions
        isInternalSkuIdAppended={isInternalSkuIdAppended ?? false}
        isLoading={isLoading}
        productLineName={name}
        productLineShortName={shortName}
        nameSeparator={nameSeparator}
      >
        <InlineFieldsWrapper mt={0}>
          <FormField name="shortName" placeholder={name || undefined} />

          <FormField
            name="nameSeparator"
            label="Separator"
            flex="0 0 6em !important"
            renderInput={(inputProps) => (
              <Input {...inputProps} fontWeight="bold" />
            )}
          />

          <ControlledFormField
            name="isInternalSkuIdAppended"
            renderInput={({ field }) => <SwitchInput {...field} />}
          />
        </InlineFieldsWrapper>
      </NamingOptions>

      <ControlledFormField
        name="productCategoryIds"
        renderInput={({ field }) => <ProductCategoriesSelect {...field} />}
      />

      <ControlledFormField
        name="manufacturingFacilityId"
        label={
          <Flex gap={2}>
            Manufacturing Facility
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={onOpenManageFacilities}
              ml="auto"
            >
              Manage…
            </Button>
          </Flex>
        }
        renderInput={({ field }) => (
          <ManufacturingFacilitySelect {...field} isClearable />
        )}
      />

      <FormField
        name="shipsFromZip"
        id="ProductLinePanel-ships-from-zip"
        renderInput={(inputProps) => (
          <Input {...inputProps} pattern="[0-9]*" maxLength={5} />
        )}
      />

      <InlineFieldsWrapper>
        <ControlledFormField
          name="innerCaseQuantity"
          id="ProductLinePanel-inner-case-quantity"
          helpText="Number of units per inner case, if applicable, otherwise leave blank."
          renderInput={({ field }) => (
            <NumberInput
              {...field}
              max={32767}
              decimalScale={0}
              allowNegative={false}
              thousandSeparator
              placeholder="N/A"
            />
          )}
        />

        <ControlledFormField
          name="wholesaleCaseQuantity"
          id="ProductLinePanel-wholesale-case-quantity"
          helpText="Number of inner cases per master case, if applicable, or number of units if not."
          renderInput={({ field }) => (
            <NumberInput
              {...field}
              max={32767}
              decimalScale={0}
              thousandSeparator
              allowNegative={false}
            />
          )}
        />
      </InlineFieldsWrapper>

      <CalculatedField
        isLoading={isLoading}
        label={`Number of units per ${innerCaseQuantity ? "master " : ""}case`}
        value={formatInteger(wholesaleCaseQuantityUnits)}
      />

      <ControlledFormField
        name="wholesaleUnitPrice"
        id="ProductLinePanel-wholesaleUnitPrice"
        helpText={
          "Individual wholesale unit prices may be set separately for each SKU at each " +
          "customer, but SKUs will default to this value where specific wholesale unit " +
          "prices are not set."
        }
        renderInput={({ field }) => (
          <NumberInput
            {...field}
            max={100000}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator
            prefix="$"
            allowNegative={false}
          />
        )}
      />

      {innerCaseQuantity && (
        <CalculatedField
          isLoading={isLoading}
          label="Default wholesale price per inner case"
          value={
            innerCaseQuantity && wholesaleUnitPrice
              ? makeFormatCurrency({
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })(innerCaseQuantity * parseFloat(wholesaleUnitPrice))
              : null
          }
        />
      )}

      <CalculatedField
        isLoading={isLoading}
        label={`Default wholesale price per ${
          innerCaseQuantity ? "master " : ""
        }case`}
        value={
          wholesaleCaseQuantityUnits && wholesaleUnitPrice
            ? makeFormatCurrency({
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })(wholesaleCaseQuantityUnits * parseFloat(wholesaleUnitPrice))
            : null
        }
      />

      <Modal
        isOpen={isManageFacilitiesOpen}
        onClose={onCloseManageFacilities}
        size="lg"
      >
        <ModalOverlay />
        <ManageManufacturingFacilitiesModalContent />
      </Modal>
    </Form>
  );
}
